import React, { useState, useEffect } from "react";
import {
  X,
  User,
  Calendar,
  ExternalLink,
  CheckCircle,
  XCircle,
  DollarSign,
  ArrowLeft,
  Link as LinkIcon,
  MessageSquare,
  FileText,
  Clock,
  Award,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { useContractServices } from "../../../context/ContractServicesContext";
import { toast } from "react-toastify";
import ReleaseIUModal from "./ReleaseIUModal";
import { taskAPI } from "../../../services/api";

const TaskSubmissionsView = ({
  task,
  project,
  onClose,
  onReview,
  onReleaseIUs,
  isCreator,
  selectedSubmission,
  setSelectedSubmission,
  getExplorerUrl,
}) => {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { services, isReady } = useContractServices();

  // Add ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && !loading) {
        handleClose(e);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [loading]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Helper function to get explorer URL with fallback
  const safeGetExplorerUrl = (txHash) => {
    if (typeof getExplorerUrl === 'function') {
      return getExplorerUrl(txHash);
    }
    // Default to Optimism Sepolia as fallback
    return `https://sepolia-optimism.etherscan.io/tx/${txHash}`;
  };

  const handleApprove = async (submissionId) => {
    setLoading(true);
    try {
      await onReview(submissionId, "approved", feedback);
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error approving submission:", error);
      toast.error("Failed to approve submission");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (submissionId) => {
    setLoading(true);
    try {
      await onReview(submissionId, "rejected", feedback);
      setFeedback("");
      onClose();
    } catch (error) {
      console.error("Error rejecting submission:", error);
      toast.error("Failed to reject submission");
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseIUs = async () => {
    // Open the ReleaseIUModal with the selected submission
    setShowReleaseModal(true);
  };
  
  // Handle the after-release update
  const handleIUsReleased = async (taskId, contributorId) => {
    try {
      // Fetch the updated task with iusReleased flag set
      const { data: updatedTask } = await taskAPI.getById(taskId);
      
      // Update the task in the UI
      if (onReleaseIUs) {
        await onReleaseIUs(taskId, contributorId);
      }
      
      // Close the modal
      setShowReleaseModal(false);
    } catch (error) {
      console.error("Error refreshing task after IU release:", error);
      toast.error("Failed to refresh task after IU release");
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusClasses = {
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200"
    };
    
    return (
      <span className={`px-3 py-1 text-xs rounded-full font-medium border ${statusClasses[status] || statusClasses.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Add a fallback close function if onClose is not provided
  const closeModal = () => {
    console.log("Using fallback close function");
    if (typeof setSelectedSubmission === 'function') {
      setSelectedSubmission(null);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) {
      console.log("Cannot close during loading state");
      return;
    }

    console.log("Closing modal");
    if (typeof onClose === 'function') {
      onClose();
    } else {
      closeModal();
    }
  };

  const handleBackdropClick = (e) => {
    // Only close if the backdrop itself was clicked (not its children)
    if (e.target === e.currentTarget && !loading) {
      handleClose(e);
    }
  };

  // If no submission is selected, show a message
  if (!selectedSubmission) {
    return (
      <div className="border border-gray-200 rounded-md p-6 bg-white">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Task Submissions</h2>
          <button type="button" onClick={handleClose} className="text-black hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No submission selected</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-xl shadow-2xl ${isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-5xl h-[85vh]'} flex flex-col animate-fadeIn`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl relative">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleFullscreen}
                className="mr-2 p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? 
                  <Minimize2 className="h-5 w-5" /> : 
                  <Maximize2 className="h-5 w-5" />
                }
              </button>
              <h2 className="text-2xl font-bold text-gray-800">Submission Details</h2>
              <StatusBadge status={selectedSubmission.status} />
            </div>
            <button 
              type="button"
              onClick={handleClose} 
              className="text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-100 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer z-50 shadow-sm"
              aria-label="Close"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-grow overflow-y-auto p-6 custom-scrollbar">
          {/* Task and Contributor Overview */}
          <div className="bg-indigo-50/50 rounded-xl p-6 mb-8 border border-indigo-100 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Task Info */}
              <div className="col-span-1 lg:col-span-2">
                <h3 className="text-lg font-semibold text-indigo-900 mb-2 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                  Task Information
                </h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                  <h4 className="font-medium text-lg text-gray-900 mb-2">{task.title}</h4>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="bg-green-100 text-green-800 rounded-full px-3 py-1 text-xs font-medium border border-green-200 flex items-center">
                      <Award className="w-3 h-3 mr-1" />
                      {task.taskPoints} IU
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium flex items-center ${
                      task.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' :
                      task.status === 'submitted' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                      'bg-indigo-100 text-indigo-800 border border-indigo-200'
                    }`}>
                      <Clock className="w-3 h-3 mr-1" />
                      {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contributor Info */}
              <div>
                <h3 className="text-lg font-semibold text-indigo-900 mb-2 flex items-center">
                  <User className="w-5 h-5 mr-2 text-indigo-600" />
                  Contributor
                </h3>
                <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                  <div className="font-medium text-gray-900 mb-1">
                    <a 
                      href={`/innovators/${selectedSubmission.contributorWalletAddress}`}
                      className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {selectedSubmission.contributorWalletAddress?.slice(0, 10)}...
                    </a>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
                    <span>Submitted {new Date(selectedSubmission.submissionDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submission content */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
              Submission Message
            </h3>
            <div className="bg-white p-5 rounded-xl border border-gray-200 text-gray-800 shadow-sm min-h-24 max-h-60 overflow-y-auto custom-scrollbar">
              {selectedSubmission.message ? (
                <p className="whitespace-pre-line">{selectedSubmission.message}</p>
              ) : (
                <p className="text-gray-500 italic">No message provided</p>
              )}
            </div>
          </div>

          {/* Submission links */}
          {selectedSubmission.links && selectedSubmission.links.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <LinkIcon className="w-5 h-5 mr-2 text-indigo-600" />
                Submission Links
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedSubmission.links.map((link, index) => (
                  <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 flex items-center shadow-sm hover:shadow-md transition-shadow">
                    <ExternalLink className="w-5 h-5 mr-3 text-indigo-500 flex-shrink-0" />
                    <a 
                      href={link.startsWith('http') ? link : `https://${link}`} 
                      className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm break-all"
                    >
                      {link}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback section */}
          {selectedSubmission.feedback && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                Feedback
              </h3>
              <div className="bg-white p-5 rounded-xl border border-gray-200 text-gray-700 shadow-sm">
                <p className="italic">"{selectedSubmission.feedback}"</p>
              </div>
            </div>
          )}

          {/* Review feedback textarea */}
          {isCreator && selectedSubmission?.status === "pending" && (
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-800 mb-3 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
                Feedback (optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                rows="4"
                placeholder="Add feedback about this submission..."
              />
            </div>
          )}
        </div>
        
        {/* Fixed footer with action buttons */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {/* Review action buttons for creator */}
          {isCreator && selectedSubmission?.status === "pending" && (
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleReject(selectedSubmission._id)}
                className="px-5 py-2.5 bg-white border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors shadow-sm"
                disabled={loading}
              >
                <XCircle className="w-5 h-5 mr-2" />
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedSubmission._id)}
                className="px-5 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors shadow-sm"
                disabled={loading}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Approve
              </button>
            </div>
          )}

          {/* Release IUs button for approved submissions */}
          {isCreator && selectedSubmission?.status === "approved" && !task.iusReleased && (
            <div className="flex justify-end">
              <button
                onClick={() => handleReleaseIUs()}
                className="px-5 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors shadow-sm"
                disabled={loading}
              >
                <DollarSign className="w-5 h-5 mr-2" />
                Release IUs
              </button>
            </div>
          )}

          {/* IUs released indicator */}
          {selectedSubmission.status === "approved" && task.iusReleased && (
            <div className="flex justify-end">
              <div className="bg-teal-50 border border-teal-200 rounded-lg px-5 py-2.5 flex items-center text-teal-800 shadow-sm">
                <DollarSign className="w-5 h-5 mr-2 text-teal-600" />
                IUs have been released for this submission
              </div>
            </div>
          )}
          
          {/* Back button for rejected or completed submissions */}
          {(selectedSubmission.status === "rejected" || 
           (selectedSubmission.status === "approved" && !isCreator)) && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center transition-colors shadow-sm"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ReleaseIUModal */}
      {showReleaseModal && (
        <ReleaseIUModal
          isOpen={showReleaseModal}
          onClose={() => setShowReleaseModal(false)}
          project={project}
          task={task}
          submission={selectedSubmission}
          onRelease={handleIUsReleased}
          getExplorerUrl={getExplorerUrl}
        />
      )}

      {/* Add CSS for custom scrollbar */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TaskSubmissionsView;
